"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { ArrowRight, Mail, MessageCircle, Phone } from "lucide-react";
import { Loader } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function AssessmentBookingForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 bg-card border border-border">
        <h3 className="text-2xl font-semibold text-foreground mb-3 font-default">Request Received</h3>
        <p className="text-foreground/80 mb-6">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="">
          Submit another request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <FormField control={control} name="first_name" rules={{
        required: "First name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-sm font-medium text-foreground/80">First Name</FormLabel>
              <FormControl>
                <Input placeholder="John" className="h-14 bg-card border-b border-border px-5 text-base focus:border-primary" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="last_name" rules={{
        required: "Last name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-sm font-medium text-foreground/80">Last Name</FormLabel>
              <FormControl>
                <Input placeholder="Doe" className="h-14 bg-card border-b border-border px-5 text-base focus:border-primary" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Invalid email"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-sm font-medium text-foreground/80">Email Address *</FormLabel>
            <FormControl>
              <Input type="email" placeholder="john@company.com" className="h-14 bg-card border-b border-border px-5 text-base focus:border-primary" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="phone" rules={{
      required: "Phone is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-sm font-medium text-foreground/80">Phone Number *</FormLabel>
            <FormControl>
              <Input type="tel" placeholder="0400 000 000" className="h-14 bg-card border-b border-border px-5 text-base focus:border-primary" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="service_interest" rules={{
      required: "Please select a service"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-sm font-medium text-foreground/80">Service Interest</FormLabel>
            <FormControl>
              <Select onValueChange={field.onChange} value={field.value}>
                <SelectTrigger className="h-14 w-full bg-card border-b border-border px-5 text-base">
                  <SelectValue placeholder="Select a service..." />
                </SelectTrigger>
                <SelectContent className="">
                  <SelectItem value="threat_detection">AI Threat Detection</SelectItem>
                  <SelectItem value="monitoring">24/7 Security Monitoring</SelectItem>
                  <SelectItem value="phishing">Phishing Prevention</SelectItem>
                  <SelectItem value="vulnerability">Vulnerability Assessment</SelectItem>
                  <SelectItem value="consulting">Security Consulting</SelectItem>
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="preferred_date" rules={{
      required: "Please select a date"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-sm font-medium text-foreground/80">Preferred Assessment Date *</FormLabel>
            <FormControl>
              <div className="bg-card p-3 sm:p-4 border border-border overflow-hidden">
                <SafeHydrate><Calendar mode="single" selected={field.value ? new Date(field.value) : undefined} onSelect={date => field.onChange(date ? date.toISOString().split("T")[0] : "")} disabled={date => date < new Date(new Date().setHours(0, 0, 0, 0))} className="bg-transparent mx-auto" /></SafeHydrate>
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-sm font-medium text-foreground/80">Additional Details</FormLabel>
            <FormControl>
              <Textarea placeholder="Tell us about your security concerns or specific requirements..." className="min-h-32 bg-card border-b border-border px-5 py-4 text-base focus:border-primary" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/30">
          <p className="text-destructive text-sm">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-14 bg-primary text-primary-foreground hover:bg-primary/90 text-base font-semibold shadow-md group">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-4" />
            Processing...
          </> : <>
            Request Assessment
            <ArrowRight className="size-5 transition-transform group-hover:translate-x-1" />
          </>}
      </Button>

      <p className="text-xs text-muted-foreground text-center pt-2">
        <span className="text-foreground">*</span> Required fields. Your data is encrypted and handled with strict confidentiality.
      </p>
    </Form>;
}
export default function Assessment() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="414" id="request-your-assessment" className="bg-background py-20 sm:py-28 lg:py-32 font-default border-t border-border">
      <div ref={ref} className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-24">
              <span className="inline-block text-xs font-mono font-medium tracking-widest text-secondary uppercase mb-6">
                § 04 / Assessment
              </span>
              <h2 className="text-5xl sm:text-6xl lg:text-7xl font-semibold tracking-tight text-foreground leading-[0.95] mb-8 font-default">
                Secure your <br />
                <span className="italic font-serif font-normal">digital future.</span>
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed mb-10 max-w-md">
                Get a comprehensive security audit from InfinitySecure. Our AI-driven analysis identifies vulnerabilities before they become threats, ensuring your business remains resilient.
              </p>

              <div className="bg-card p-6 sm:p-7 border border-border">
                <p className="text-xs font-mono font-medium tracking-widest text-muted-foreground uppercase mb-5">
                  Direct Contact
                </p>
                <ul className="space-y-4">
                  {[{
                  Icon: Phone,
                  label: "Call us",
                  value: "0468 086 883"
                }, {
                  Icon: Mail,
                  label: "Email",
                  value: "info@infinitysecure.net"
                }, {
                  Icon: MessageCircle,
                  label: "Support",
                  value: "24/7 Security Operations"
                }].map((item, i) => {
                  const Icon = item.Icon;
                  return <li key={i} data-index={i} className="flex items-start gap-4">
                        <div className="flex-shrink-0 size-10 bg-background flex items-center justify-center border border-border">
                          <Icon className="size-4 text-primary" />
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground mb-0.5">{item.label}</p>
                          <p className="text-sm font-medium text-foreground font-default">
                            {item.value}
                          </p>
                        </div>
                      </li>;
                })}
                </ul>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <FormProvider formKey="assessment_form_contact" formId="assessment_form_contact" sectionName="request-your-assessment" className="bg-card p-6 sm:p-10 lg:p-12 border border-border" defaultValues={{
            first_name: "",
            last_name: "",
            email: "",
            phone: "",
            service_interest: "",
            preferred_date: "",
            message: ""
          }} validationRules={{
            first_name: {
              required: true,
              minLength: 2
            },
            last_name: {
              required: true,
              minLength: 2
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              required: true,
              pattern: "phone"
            },
            service_interest: {
              required: true
            },
            preferred_date: {
              required: true
            }
          }} submitText="Request Assessment" successMessage="Request received. Our security team will contact you within 24 hours to schedule your assessment." errorMessage="Submission failed. Please check your details or contact us directly.">
              <AssessmentBookingForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}